import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Heart,
  Lightbulb,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  Zap,
} from 'lucide-react';
import {
  type CoreValue,
  type DisgustExperience,
  type DisgustIntensity,
  type ValuesFromDisgustState,
  type ValuesFromDisgustStep,
  INTENSITY_LABELS,
  MAX_CORE_VALUES,
  MIN_EXPERIENCES,
  TARGET_EXPERIENCES,
  VALUES_FROM_DISGUST_STEPS,
  canAdvance,
  clusterOppositeValues,
  createCoreValue,
  createExperience,
  nextStep,
  prevStep,
  stepIndex,
  suggestOppositeValues,
} from '../lib/valuesFromDisgust';

const STORAGE_KEY = 'lab_values_from_disgust_v02';

const DEFAULT_STATE: ValuesFromDisgustState = {
  experiences: [createExperience(), createExperience()],
  coreValues: [],
};

function loadState(): ValuesFromDisgustState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<ValuesFromDisgustState>;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      experiences:
        parsed.experiences?.length ? parsed.experiences : DEFAULT_STATE.experiences,
      coreValues: parsed.coreValues ?? [],
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveState(state: ValuesFromDisgustState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface UnderstandingYourValuesProps {
  /** When embedded in What's Next, hide the lab chrome */
  embedded?: boolean;
  onComplete?: (state: ValuesFromDisgustState) => void;
}

export const UnderstandingYourValues: React.FC<UnderstandingYourValuesProps> = ({
  embedded = false,
  onComplete,
}) => {
  const [step, setStep] = useState<ValuesFromDisgustStep>('intro');
  const [state, setState] = useState<ValuesFromDisgustState>(loadState);
  const [activeExperienceId, setActiveExperienceId] = useState<string | null>(null);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const progressPct = useMemo(
    () => ((stepIndex(step) + 1) / VALUES_FROM_DISGUST_STEPS.length) * 100,
    [step],
  );

  const updateExperience = useCallback((id: string, updates: Partial<DisgustExperience>) => {
    setState(prev => ({
      ...prev,
      experiences: prev.experiences.map(e => (e.id === id ? { ...e, ...updates } : e)),
    }));
  }, []);

  const addExperience = useCallback(() => {
    const exp = createExperience();
    setState(prev => ({ ...prev, experiences: [...prev.experiences, exp] }));
    setActiveExperienceId(exp.id);
  }, []);

  const removeExperience = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      experiences:
        prev.experiences.length <= MIN_EXPERIENCES
          ? prev.experiences
          : prev.experiences.filter(e => e.id !== id),
      coreValues: prev.coreValues.map(v => ({
        ...v,
        sourceExperienceIds: v.sourceExperienceIds.filter(sid => sid !== id),
      })),
    }));
  }, []);

  const updateCoreValue = useCallback((id: string, updates: Partial<CoreValue>) => {
    setState(prev => ({
      ...prev,
      coreValues: prev.coreValues.map(v => (v.id === id ? { ...v, ...updates } : v)),
    }));
  }, []);

  const removeCoreValue = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      coreValues: prev.coreValues.filter(v => v.id !== id),
    }));
  }, []);

  const goNext = useCallback(() => {
    if (!canAdvance(step, state)) return;

    if (step === 'flip') {
      const clustered = clusterOppositeValues(state.experiences).slice(0, MAX_CORE_VALUES);
      setState(prev => ({ ...prev, coreValues: clustered }));
    }

    if (step === 'distill') {
      const completed = {
        ...state,
        completedAt: new Date().toISOString(),
      };
      setState(completed);
      onComplete?.(completed);
    }

    const n = nextStep(step);
    if (n) setStep(n);
  }, [step, state, onComplete]);

  const goBack = useCallback(() => {
    const p = prevStep(step);
    if (p) setStep(p);
  }, [step]);

  const reset = useCallback(() => {
    const fresh = {
      experiences: [createExperience(), createExperience()],
      coreValues: [],
    };
    setState(fresh);
    setStep('intro');
    setActiveExperienceId(null);
  }, []);

  const experienceById = useMemo(
    () => new Map(state.experiences.map(e => [e.id, e])),
    [state.experiences],
  );

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-8'}>
      {!embedded && (
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Heart className="h-8 w-8 text-consulting-slate-blue-600" />
            <h1 className="text-3xl font-heading font-bold text-charcoal-900">
              Understanding Your Values
            </h1>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
              v0.2
            </span>
          </div>
          <p className="text-charcoal-600 text-sm max-w-2xl mx-auto">
            Start with disgust, not aspiration. Strong revulsion is specific — and the opposite
            of what revolted you is often a value you already protect.
          </p>
        </div>
      )}

      {embedded && (
        <div className="mb-2">
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-widest mb-2">
            Values discovery · v0.2
          </div>
          <h2 className="font-serif text-2xl text-text-primary font-light">
            Understanding Your Values
          </h2>
          <p className="font-sans text-text-secondary text-sm mt-2 max-w-2xl">
            Skip the word salad. Start with experiences that still make your skin crawl — then
            name what they violated. The opposite is your value.
          </p>
        </div>
      )}

      {/* Progress */}
      <div className={embedded ? '' : 'max-w-3xl mx-auto w-full'}>
        <div className="flex items-center justify-between mb-2">
          <span
            className={`font-mono text-[10px] uppercase tracking-widest ${
              embedded ? 'text-text-dim' : 'text-charcoal-500'
            }`}
          >
            Step {stepIndex(step) + 1} of {VALUES_FROM_DISGUST_STEPS.length} —{' '}
            {VALUES_FROM_DISGUST_STEPS[stepIndex(step)]?.label}
          </span>
          <span className={`font-mono text-[10px] ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            {Math.round(progressPct)}%
          </span>
        </div>
        <div
          className={`h-1.5 rounded-full overflow-hidden ${
            embedded ? 'bg-surface-elevated' : 'bg-charcoal-100'
          }`}
        >
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              embedded ? 'bg-[var(--green)]' : 'bg-consulting-slate-blue-600'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div
        className={` border p-6 md:p-8 ${
          embedded
            ? 'bg-surface-card border-surface-border'
            : 'bg-[var(--bg-raised)] border-charcoal-200 max-w-3xl mx-auto'
        }`}
      >
        {step === 'intro' && <IntroStep embedded={embedded} />}
        {step === 'recall' && (
          <RecallStep
            embedded={embedded}
            experiences={state.experiences}
            activeId={activeExperienceId}
            onSetActive={setActiveExperienceId}
            onUpdate={updateExperience}
            onAdd={addExperience}
            onRemove={removeExperience}
          />
        )}
        {step === 'violation' && (
          <ViolationStep
            embedded={embedded}
            experiences={state.experiences}
            onUpdate={updateExperience}
          />
        )}
        {step === 'flip' && (
          <FlipStep embedded={embedded} experiences={state.experiences} onUpdate={updateExperience} />
        )}
        {step === 'distill' && (
          <DistillStep
            embedded={embedded}
            experiences={state.experiences}
            coreValues={state.coreValues}
            onUpdate={updateCoreValue}
            onAdd={() =>
              setState(prev => ({
                ...prev,
                coreValues:
                  prev.coreValues.length >= MAX_CORE_VALUES
                    ? prev.coreValues
                    : [...prev.coreValues, createCoreValue()],
              }))
            }
            onRemove={removeCoreValue}
          />
        )}
        {step === 'results' && (
          <ResultsStep
            embedded={embedded}
            state={state}
            experienceById={experienceById}
          />
        )}
      </div>

      {/* Navigation */}
      <div
        className={`flex items-center justify-between gap-4 ${
          embedded ? '' : 'max-w-3xl mx-auto'
        }`}
      >
        <button
          type="button"
          onClick={goBack}
          disabled={step === 'intro'}
          className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            embedded
              ? 'border-surface-border text-text-tertiary hover:bg-surface-elevated'
              : 'border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-2">
          {step === 'results' ? (
            <button
              type="button"
              onClick={reset}
              className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium ${
                embedded
                  ? 'border-surface-border text-text-tertiary hover:bg-surface-elevated'
                  : 'border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
              }`}
            >
              <RotateCcw className="h-4 w-4" />
              Start over
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance(step, state)}
              className={`inline-flex items-center gap-2 px-5 py-2  text-sm font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                embedded
                  ? 'bg-[var(--green)] hover:bg-[var(--green)]'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {step === 'distill' ? 'See my values' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function IntroStep({ embedded }: { embedded: boolean }) {
  const card = embedded
    ? 'bg-surface-elevated border-surface-border text-text-secondary'
    : 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-charcoal-700';
  const heading = embedded ? 'text-text-primary' : 'text-charcoal-900';

  return (
    <div className="space-y-6">
      <div className={` border p-5 ${card}`}>
        <div className="flex items-start gap-3">
          <Zap className={`h-5 w-5 flex-shrink-0 mt-0.5 ${embedded ? 'text-[var(--green)]' : 'text-[var(--mustard)]'}`} />
          <div>
            <h2 className={`font-semibold mb-2 ${heading}`}>The problem with values lists</h2>
            <p className="text-sm leading-relaxed">
              Most exercises hand you 100 words that all sound like you — or like the person you
              want to be. Pick five. It is crazymaking. Everything feels true. Nothing discriminates.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 text-sm leading-relaxed">
        <p className={embedded ? 'text-text-secondary' : 'text-charcoal-700'}>
          <strong className={heading}>Start somewhere unexpected:</strong> past experiences that
          evoked a strong sense of disgust. Not mild annoyance — the kind that still makes your
          stomach turn when you remember it.
        </p>
        <p className={embedded ? 'text-text-secondary' : 'text-charcoal-700'}>
          Disgust is specific. It points at a violation. Name what was violated — then flip it.
          The opposite is often a value you already live by, fight for, or refuse to compromise on.
        </p>
      </div>

      <div
        className={`grid sm:grid-cols-3 gap-3 text-sm ${
          embedded ? 'text-text-tertiary' : 'text-charcoal-600'
        }`}
      >
        {[
          { n: '1', t: 'Recall 2–3 disgust moments', d: 'Work, life, anywhere they still sting' },
          { n: '2', t: 'Name the violation', d: 'What exactly crossed a line for you?' },
          { n: '3', t: 'Flip to your values', d: 'Distill into your top 5' },
        ].map(item => (
          <div
            key={item.n}
            className={` border p-4 ${
              embedded ? 'bg-surface-elevated border-surface-border' : 'bg-[var(--surface)] border-charcoal-100'
            }`}
          >
            <div
              className={`font-mono text-xs mb-2 ${
                embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'
              }`}
            >
              {item.n}
            </div>
            <div className={`font-medium mb-1 ${heading}`}>{item.t}</div>
            <div className="text-xs">{item.d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecallStep({
  embedded,
  experiences,
  activeId,
  onSetActive,
  onUpdate,
  onAdd,
  onRemove,
}: {
  embedded: boolean;
  experiences: DisgustExperience[];
  activeId: string | null;
  onSetActive: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<DisgustExperience>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}) {
  const label = embedded ? 'text-text-dim' : 'text-charcoal-500';
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
        Think of {MIN_EXPERIENCES}–{TARGET_EXPERIENCES} moments when something felt{' '}
        <em>morally or viscerally wrong</em> — not just inconvenient. A boss who humiliated someone.
        A team that celebrated cutting corners. A place where lying was normal.
      </p>

      <div className="space-y-4">
        {experiences.map((exp, idx) => {
          const isOpen = activeId === exp.id || activeId === null;
          return (
            <div
              key={exp.id}
              className={` border overflow-hidden ${
                embedded ? 'border-surface-border' : 'border-charcoal-200'
              }`}
            >
              <div
                className={`flex items-center justify-between px-4 py-3 ${
                  embedded ? 'bg-surface-elevated' : 'bg-[var(--surface)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSetActive(isOpen && activeId === exp.id ? null : exp.id)}
                  className={`flex-1 text-left text-sm font-medium ${
                    embedded ? 'text-text-primary' : 'text-charcoal-900'
                  }`}
                >
                  Experience {idx + 1}
                  {exp.whatHappened.trim() && (
                    <span className={`font-normal ml-2 ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
                      — {exp.whatHappened.trim().slice(0, 48)}
                      {exp.whatHappened.length > 48 ? '…' : ''}
                    </span>
                  )}
                </button>
                {experiences.length > MIN_EXPERIENCES && (
                  <button
                    type="button"
                    onClick={() => onRemove(exp.id)}
                    className={`p-1 rounded ml-2 ${
                      embedded ? 'text-text-dim hover:text-[var(--coral)]' : 'text-charcoal-400 hover:text-[var(--coral)]'
                    }`}
                    aria-label="Remove experience"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="p-4 space-y-4 border-t border-inherit">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Roughly when?" embedded={embedded}>
                      <input
                        value={exp.when}
                        onChange={e => onUpdate(exp.id, { when: e.target.value })}
                        placeholder="e.g. 2019, first VP role"
                        className={`w-full  border px-3 py-2 text-sm focus:outline-none ${input}`}
                      />
                    </Field>
                    <Field label="Context" embedded={embedded}>
                      <input
                        value={exp.context}
                        onChange={e => onUpdate(exp.id, { context: e.target.value })}
                        placeholder="e.g. All-hands at Acme Corp"
                        className={`w-full  border px-3 py-2 text-sm focus:outline-none ${input}`}
                      />
                    </Field>
                  </div>

                  <Field label="What happened?" embedded={embedded}>
                    <textarea
                      value={exp.whatHappened}
                      onChange={e => onUpdate(exp.id, { whatHappened: e.target.value })}
                      placeholder="Stick to what a recording could capture — then how it landed in your body."
                      rows={3}
                      className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
                    />
                  </Field>

                  <Field label="How strong was the disgust?" embedded={embedded}>
                    <div className="flex flex-wrap gap-2">
                      {([1, 2, 3, 4, 5] as DisgustIntensity[]).map(level => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => onUpdate(exp.id, { intensity: level })}
                          className={`px-3 py-1.5  border text-xs font-medium transition-colors ${
                            exp.intensity === level
                              ? embedded
                                ? 'border-[var(--green)]/50 bg-[var(--green)]/10 text-[var(--green)] dark:text-[var(--text-ter)]'
                                : 'border-consulting-slate-blue-400 bg-consulting-slate-blue-50 text-consulting-slate-blue-800'
                              : embedded
                                ? 'border-surface-border text-text-dim hover:border-surface-border-bright'
                                : 'border-charcoal-200 text-charcoal-600 hover:border-charcoal-300'
                          }`}
                        >
                          {level} · {INTENSITY_LABELS[level]}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onAdd}
        className={`inline-flex items-center gap-2 text-sm font-medium ${
          embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]' : 'text-primary-600 hover:text-primary-700'
        }`}
      >
        <Plus className="h-4 w-4" />
        Add another experience
      </button>

      <p className={`text-xs ${label}`}>
        Minimum {MIN_EXPERIENCES} experiences with when, context, and what happened filled in.
      </p>
    </div>
  );
}

function ViolationStep({
  embedded,
  experiences,
  onUpdate,
}: {
  embedded: boolean;
  experiences: DisgustExperience[];
  onUpdate: (id: string, updates: Partial<DisgustExperience>) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
        For each experience, name the <strong>specific violation</strong> — not a judgment of the
        person, but what line was crossed. &ldquo;They were bad&rdquo; is too vague. &ldquo;They
        took credit for my team&apos;s work in front of the board&rdquo; is usable.
      </p>

      <div className="space-y-5">
        {experiences.map((exp, idx) => (
          <div
            key={exp.id}
            className={` border p-4 space-y-3 ${
              embedded ? 'border-surface-border bg-surface-elevated/50' : 'border-charcoal-200 bg-[var(--surface)]'
            }`}
          >
            <div className={`text-xs font-mono uppercase tracking-wider ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
              Experience {idx + 1} · {exp.context || 'No context yet'}
            </div>
            <p className={`text-sm ${embedded ? 'text-text-tertiary' : 'text-charcoal-600'}`}>
              {exp.whatHappened || '—'}
            </p>
            <Field label="What specifically disgusted you?" embedded={embedded}>
              <textarea
                value={exp.violation}
                onChange={e => onUpdate(exp.id, { violation: e.target.value })}
                placeholder="The line that was crossed — be specific"
                rows={2}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
          </div>
        ))}
      </div>
    </div>
  );
}

function FlipStep({
  embedded,
  experiences,
  onUpdate,
}: {
  embedded: boolean;
  experiences: DisgustExperience[];
  onUpdate: (id: string, updates: Partial<DisgustExperience>) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <div
        className={` border p-4 flex items-start gap-3 ${
          embedded
            ? 'bg-[var(--green)]/5 border-[var(--green)]/20'
            : 'bg-consulting-slate-blue-50 border-consulting-slate-blue-200'
        }`}
      >
        <Lightbulb className={`h-5 w-5 flex-shrink-0 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
          <strong>Hint:</strong> the opposite of the violation is the value. If the violation was
          &ldquo;public humiliation,&rdquo; the value might be dignity or respect. Use your words —
          suggestions are starting points only.
        </p>
      </div>

      <div className="space-y-5">
        {experiences.map((exp, idx) => {
          const hints = suggestOppositeValues(exp.violation);
          return (
            <div
              key={exp.id}
              className={` border p-4 space-y-3 ${
                embedded ? 'border-surface-border' : 'border-charcoal-200'
              }`}
            >
              <div className={`text-xs ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
                <span className="font-mono uppercase tracking-wider">Violation · </span>
                {exp.violation || '—'}
              </div>

              <Field label={`Experience ${idx + 1} — your value (the opposite)`} embedded={embedded}>
                <input
                  value={exp.oppositeValue}
                  onChange={e => onUpdate(exp.id, { oppositeValue: e.target.value })}
                  placeholder="e.g. Dignity, Fair process, Real autonomy"
                  className={`w-full  border px-3 py-2 text-sm focus:outline-none ${input}`}
                />
              </Field>

              {hints.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {hints.map(hint => (
                    <button
                      key={hint}
                      type="button"
                      onClick={() => onUpdate(exp.id, { oppositeValue: hint })}
                      className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                        exp.oppositeValue === hint
                          ? embedded
                            ? 'border-[var(--green)]/50 bg-[var(--green)]/10 text-[var(--green)] dark:text-[var(--text-ter)]'
                            : 'border-primary-400 bg-primary-50 text-primary-800'
                          : embedded
                            ? 'border-surface-border text-text-dim hover:border-[var(--green)]/30'
                            : 'border-charcoal-200 text-charcoal-600 hover:border-charcoal-300'
                      }`}
                    >
                      {hint}
                    </button>
                  ))}
                </div>
              )}

              <Field label="What does honoring this look like in practice?" embedded={embedded}>
                <textarea
                  value={exp.oppositeInPractice}
                  onChange={e => onUpdate(exp.id, { oppositeInPractice: e.target.value })}
                  placeholder="When this value is alive, people..."
                  rows={2}
                  className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
                />
              </Field>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DistillStep({
  embedded,
  experiences,
  coreValues,
  onUpdate,
  onAdd,
  onRemove,
}: {
  embedded: boolean;
  experiences: DisgustExperience[];
  coreValues: CoreValue[];
  onUpdate: (id: string, updates: Partial<CoreValue>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
        We grouped your flipped values by similarity. Edit labels, merge duplicates, and keep at most{' '}
        {MAX_CORE_VALUES} that feel most essential. These should discriminate — if everything is
        sacred, nothing guides decisions.
      </p>

      <div className="space-y-4">
        {coreValues.map((value, idx) => (
          <div
            key={value.id}
            className={` border p-4 space-y-3 ${
              embedded ? 'border-surface-border bg-surface-elevated/40' : 'border-charcoal-200'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`font-mono text-[10px] uppercase tracking-widest ${embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'}`}>
                Core value {idx + 1}
              </span>
              {coreValues.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemove(value.id)}
                  className={embedded ? 'text-text-dim hover:text-[var(--coral)]' : 'text-charcoal-400 hover:text-[var(--coral)]'}
                  aria-label="Remove value"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            <Field label="Value name" embedded={embedded}>
              <input
                value={value.label}
                onChange={e => onUpdate(value.id, { label: e.target.value })}
                className={`w-full  border px-3 py-2 text-sm font-medium focus:outline-none ${input}`}
              />
            </Field>

            <Field label="In your own words — why does this matter?" embedded={embedded}>
              <textarea
                value={value.definition}
                onChange={e => onUpdate(value.id, { definition: e.target.value })}
                rows={2}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>

            {value.sourceExperienceIds.length > 0 && (
              <div className={`text-xs space-y-1 ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
                <span className="font-mono uppercase tracking-wider">Surfaced from:</span>
                {value.sourceExperienceIds.map(id => {
                  const exp = experiences.find(e => e.id === id);
                  if (!exp) return null;
                  return (
                    <div key={id} className="pl-2 border-l-2 border-inherit">
                      {exp.violation || exp.whatHappened}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {coreValues.length < MAX_CORE_VALUES && (
        <button
          type="button"
          onClick={onAdd}
          className={`inline-flex items-center gap-2 text-sm font-medium ${
            embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]' : 'text-primary-600 hover:text-primary-700'
          }`}
        >
          <Plus className="h-4 w-4" />
          Add a value
        </button>
      )}
    </div>
  );
}

function ResultsStep({
  embedded,
  state,
  experienceById,
}: {
  embedded: boolean;
  state: ValuesFromDisgustState;
  experienceById: Map<string, DisgustExperience>;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Sparkles className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Your values — earned, not aspirational
        </h2>
      </div>

      <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        These came from what revolted you, not from a word cloud. Use them when evaluating roles,
        teams, and tradeoffs — especially when something &ldquo;looks fine on paper.&rdquo;
      </p>

      <div className="space-y-4">
        {state.coreValues.map((value, idx) => {
          const sources = value.sourceExperienceIds
            .map(id => experienceById.get(id))
            .filter(Boolean) as DisgustExperience[];

          return (
            <div
              key={value.id}
              className={` border p-5 ${
                embedded
                  ? 'border-[var(--green)]/20 bg-[var(--green)]/5'
                  : 'border-consulting-slate-blue-200 bg-consulting-slate-blue-50/50'
              }`}
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span
                  className={`font-mono text-xs ${embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'}`}
                >
                  {idx + 1}
                </span>
                <h3 className={`text-lg font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
                  {value.label}
                </h3>
              </div>

              {value.definition && (
                <p className={`text-sm mb-3 ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
                  {value.definition}
                </p>
              )}

              {sources.length > 0 && (
                <div className={`text-xs space-y-2 ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
                  <div className="font-mono uppercase tracking-wider">Revealed by disgust at:</div>
                  {sources.map(s => (
                    <div
                      key={s.id}
                      className={`pl-3 border-l-2 ${embedded ? 'border-[var(--green)]/30' : 'border-consulting-slate-blue-300'}`}
                    >
                      <div>{s.violation}</div>
                      {s.oppositeInPractice && (
                        <div className={`mt-1 italic ${embedded ? 'text-text-tertiary' : 'text-charcoal-600'}`}>
                          Honored when: {s.oppositeInPractice}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!embedded && (
        <p className="text-xs text-charcoal-500">
          Compare with the traditional values sort in What&apos;s Next (port 3201) — your disgust-derived
          values should feel sharper than words you picked because they sounded noble.
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  embedded,
  children,
}: {
  label: string;
  embedded: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span
        className={`text-xs font-medium uppercase tracking-wider ${
          embedded ? 'text-text-dim' : 'text-charcoal-500'
        }`}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
